import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CommunicationPanel = ({ communications, onClose }) => {
  const [selectedMessage, setSelectedMessage] = useState(null);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-IN', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'email':
        return 'Mail';
      case 'notification':
        return 'Bell';
      case 'update':
        return 'RefreshCw';
      default:
        return 'MessageSquare';
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[1200] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">Communication History</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {communications?.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="MessageSquare" size={32} className="text-muted-foreground" />
              </div>
              <p className="text-muted-foreground">No communications yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {communications?.map((comm) => (
                <div
                  key={comm?.id}
                  className={`border border-border rounded-lg p-4 cursor-pointer transition-smooth ${
                    comm?.isRead ? 'bg-card' : 'bg-blue-50 dark:bg-blue-900/20'
                  } ${selectedMessage?.id === comm?.id ? 'ring-2 ring-primary' : 'hover:shadow-md'}`}
                  onClick={() => setSelectedMessage(selectedMessage?.id === comm?.id ? null : comm)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      comm?.isRead ? 'bg-muted' : 'bg-primary/10'
                    }`}>
                      <Icon 
                        name={getMessageIcon(comm?.type)} 
                        size={20} 
                        className={comm?.isRead ? 'text-muted-foreground' : 'text-primary'}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className={`text-sm font-semibold ${
                          comm?.isRead ? 'text-foreground' : 'text-primary'
                        }`}>
                          {comm?.subject}
                        </h3>
                        {!comm?.isRead && (
                          <span className="inline-flex items-center px-2 py-1 bg-primary text-primary-foreground rounded-full text-xs font-medium">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{comm?.sender}</p>
                      <p className="text-sm text-foreground line-clamp-2">{comm?.preview}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                        <span>{formatDate(comm?.date)}</span>
                        <span className="capitalize">{comm?.type}</span>
                      </div>
                    </div>
                  </div>

                  {selectedMessage?.id === comm?.id && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="prose prose-sm max-w-none">
                        <p className="text-sm text-foreground whitespace-pre-wrap">{comm?.fullMessage}</p>
                      </div>
                      {comm?.attachments && comm?.attachments?.length > 0 && (
                        <div className="mt-4">
                          <p className="text-xs font-semibold text-foreground mb-2">Attachments:</p>
                          <div className="space-y-2">
                            {comm?.attachments?.map((attachment, index) => (
                              <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                                <Icon name="Paperclip" size={16} className="text-muted-foreground" />
                                <span className="text-sm text-foreground flex-1">{attachment?.name}</span>
                                <Button variant="ghost" size="sm" iconName="Download">
                                  Download
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-border">
          <Button variant="outline" fullWidth onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommunicationPanel;